import React, { useState } from 'react';
import {
  Paper,
  Typography,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  Grid,
  Button,
  Box,
  Divider,
  Avatar,
  Chip,
  useTheme,
  alpha,
  Fade,
  Grow,
  InputAdornment,
} from '@mui/material';
import {
  Send as SendIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Description as DescriptionIcon,
  ContactMail as ContactMailIcon,
  Feedback as FeedbackIcon,
  Lightbulb as LightbulbIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import AuthService from "../Auth/AuthService";

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  // marginTop: theme.spacing(5),
  marginBottom: theme.spacing(2),
  maxWidth: 850,
  marginLeft: 'auto',
  marginRight: 'auto',
  // borderRadius: theme.spacing(3),
  background: `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.light, 0.03)} 100%)`,
  // boxShadow: `0 20px 60px ${alpha(theme.palette.primary.main, 0.15)}`,
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  },
}));

const HeaderBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  // marginBottom: theme.spacing(3),
  paddingBottom: theme.spacing(1),
  borderBottom: `2px solid ${alpha(theme.palette.divider, 0.1)}`,
}));

const HeaderAvatar = styled(Avatar)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
  width: 40,
  height: 40,
  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
}));

const StyledFormGroup = styled(FormGroup)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(2),
  flexWrap: 'wrap',
  '& .MuiFormControlLabel-root': {
    marginRight: 0,
    padding: theme.spacing(0.5, 1.5),
    borderRadius: theme.spacing(3),
    transition: 'all 0.2s ease',
    '&:hover': {
      background: alpha(theme.palette.primary.main, 0.04),
    },
  },
}));

const StyledCheckbox = styled(Checkbox)(({ theme }) => ({
  '&.Mui-checked': {
    padding: theme.spacing(1),
    color: theme.palette.primary.main,
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(2),
    transition: 'all 0.2s ease',
    padding: 0,
    '&:hover': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
        borderWidth: '2px',
      },
    },
    '&.Mui-focused': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
        borderWidth: '2px',
      },
    },
  },
  '& .MuiInputLabel-root': {
    '&.Mui-focused': {
      color: theme.palette.primary.main,
      fontWeight: 600,
    },
  },
  '& .MuiInputAdornment-root': {
    marginTop: '0 !important',
  },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1),
  borderRadius: theme.spacing(1),
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
  // boxShadow: `0 8px 30px ${alpha(theme.palette.primary.main, 0.35)}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.45)}`,
  },
  '&:active': {
    transform: 'translateY(0px)',
  },
}));

const ContactForm = ({onSuccess}) => {
  const theme = useTheme();

  const [formData, setFormData] = useState({
    contactType: ['contactus'],
    fullName: '',
    organization: '',
    mobile: '',
    cityState: '',
    description: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;

    setFormData((prev) => {
      let updatedContactType = [...prev.contactType];

      if (checked) {
        if (!updatedContactType.includes(name)) {
          updatedContactType.push(name);
        }
      } else {
        updatedContactType = updatedContactType.filter((item) => item !== name);
      }

      return {
        ...prev,
        contactType: updatedContactType,
      };
    });
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    console.log('Form Data:', formData);
    console.log('Contact Types:', formData.contactType);
    const url = AuthService.pathUrl('POST', '/contacts');
    const result = AuthService.restPost(url, formData);
    console.log(result);
    alert('✅ Form submitted successfully!');
    setIsSubmitting(false);
    if (onSuccess) onSuccess();
  };

  const getSelectedLabels = () => {
    const labelMap = {
      contactus: 'Contact Us',
      feedback: 'Feedback',
      suggestion: 'Suggestion',
    };
    return formData.contactType.map((key) => labelMap[key]).filter(Boolean);
  };

  const isSelected = (option) => {
    return formData.contactType.includes(option);
  };

  return (
    <Box maxWidth="sm">
      <Fade in timeout={600}>
        <StyledPaper elevation={0}>
          <HeaderBox>
            <HeaderAvatar>
              <ContactMailIcon sx={{ fontSize: 30 }} />
            </HeaderAvatar>
            <Box>
              <Typography variant="h5" component="h2" fontWeight={700} color="primary">
                Get in Touch
              </Typography>
              <Typography variant="body2" color="textSecondary">
                We'd love to hear from you
              </Typography>
            </Box>
          </HeaderBox>

          <form onSubmit={handleSubmit}>
            {/* Checkbox Section */}
            <FormControl component="fieldset" fullWidth>
              <FormLabel
                component="legend"
                sx={{
                  fontWeight: 600,
                  mb: 1.5,
                  fontSize: '0.95rem',
                  color: theme.palette.text.primary,
                }}
              >
                I would like to:
              </FormLabel>
              <StyledFormGroup>
                <FormControlLabel
                  control={
                    <StyledCheckbox
                      checked={isSelected('contactus')}
                      onChange={handleCheckboxChange}
                      name="contactus"
                      icon={<ContactMailIcon />}
                      checkedIcon={<ContactMailIcon />}
                    />
                  }
                  label="Contact Us"
                  sx={{
                    background: isSelected('contactus')
                      ? alpha(theme.palette.primary.main, 0.08)
                      : 'transparent',
                    borderRadius: 2,
                    '& .MuiFormControlLabel-label': {
                      fontWeight: isSelected('contactus') ? 600 : 400,
                      color: isSelected('contactus')
                        ? theme.palette.primary.main
                        : theme.palette.text.primary,
                    },
                  }}
                />
                <FormControlLabel
                  control={
                    <StyledCheckbox
                      checked={isSelected('feedback')}
                      onChange={handleCheckboxChange}
                      name="feedback"
                      icon={<FeedbackIcon />}
                      checkedIcon={<FeedbackIcon />}
                    />
                  }
                  label="Feedback"
                  sx={{
                    background: isSelected('feedback')
                      ? alpha(theme.palette.primary.main, 0.08)
                      : 'transparent',
                    borderRadius: 2,
                    '& .MuiFormControlLabel-label': {
                      fontWeight: isSelected('feedback') ? 600 : 400,
                      color: isSelected('feedback')
                        ? theme.palette.primary.main
                        : theme.palette.text.primary,
                    },
                  }}
                />
                <FormControlLabel
                  control={
                    <StyledCheckbox
                      checked={isSelected('suggestion')}
                      onChange={handleCheckboxChange}
                      name="suggestion"
                      icon={<LightbulbIcon />}
                      checkedIcon={<LightbulbIcon />}
                    />
                  }
                  label="Suggestion"
                  sx={{
                    background: isSelected('suggestion')
                      ? alpha(theme.palette.primary.main, 0.08)
                      : 'transparent',
                    borderRadius: 2,
                    '& .MuiFormControlLabel-label': {
                      fontWeight: isSelected('suggestion') ? 600 : 400,
                      color: isSelected('suggestion')
                        ? theme.palette.primary.main
                        : theme.palette.text.primary,
                    },
                  }}
                />
              </StyledFormGroup>
            </FormControl>

            {/* Selected options display */}
            {getSelectedLabels().length > 0 && (
              <Grow in timeout={400}>
                <Box sx={{ mb: 2.5, mt: 0.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {getSelectedLabels().map((label) => (
                    <Chip
                      key={label}
                      label={label}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ borderRadius: 2 }}
                    />
                  ))}
                </Box>
              </Grow>
            )}

            <Divider sx={{ my: 2.5 }} />

            {/* Input Fields */}
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <StyledTextField
                  fullWidth
                  required
                  label="Full Name"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  variant="outlined"
                  placeholder="Enter your full name"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ ml: 1 }}>
                        <PersonIcon sx={{ color: 'text.secondary', fontSize: 22 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <StyledTextField
                  fullWidth
                  required
                  label="School / Organization"
                  name="organization"
                  value={formData.organization}
                  onChange={handleInputChange}
                  variant="outlined"
                  placeholder="Enter your school or organization name"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ ml: 1 }}>
                        <SchoolIcon sx={{ color: 'text.secondary', fontSize: 22 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <StyledTextField
                  fullWidth
                  required
                  label="Mobile Number"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  variant="outlined"
                  placeholder="Enter your mobile number"
                  type="tel"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ ml: 1 }}>
                        <PhoneIcon sx={{ color: 'text.secondary', fontSize: 22 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <StyledTextField
                  fullWidth
                  required
                  label="City / State"
                  name="cityState"
                  value={formData.cityState}
                  onChange={handleInputChange}
                  variant="outlined"
                  placeholder="Enter your city and state"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ ml: 1 }}>
                        <LocationIcon sx={{ color: 'text.secondary', fontSize: 22 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <StyledTextField
                  fullWidth
                  required
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  variant="outlined"
                  placeholder="Please describe your query in detail..."
                  multiline
                  rows={4}
                  helperText="Provide detailed information about your inquiry"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment
                        position="start"
                        sx={{
                          ml: 1,
                          alignSelf: 'flex-start',
                          mt: '12px !important',
                        }}
                      >
                        <DescriptionIcon sx={{ color: 'text.secondary', fontSize: 22 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3.5, display: 'flex', justifyContent: 'center' }}>
              <SubmitButton
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disabled={isSubmitting}
                endIcon={!isSubmitting && <SendIcon />}
                sx={{ minWidth: 200 }}
              >
                {isSubmitting ? 'Submitting...' : 'Send Message'}
              </SubmitButton>
            </Box>
          </form>
        </StyledPaper>
      </Fade>
    </Box>
  );
};

ContactForm.propTypes = {};

export default ContactForm;